import React, { useRef, useEffect } from "react";
import { Package, UpdatePackageInputs } from "@/services/admin/packages.interfaces";
import { getPackages } from "@/services/admin/packages.service";
import { AutoSaveForm } from "@/components/forms/AutoSaveForm";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageSelector } from "@/components/ui/image-selector";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { extractImageFilename } from "@/lib/media";

interface PackageInfoFormProps {
  packageData: Package;
  onSave: (data: UpdatePackageInputs) => Promise<void>;
}

const PackageInfoForm: React.FC<PackageInfoFormProps> = ({
  packageData,
  onSave,
}) => {
  const formRef = useRef<HTMLDivElement>(null);
  const [isActiveValue, setIsActiveValue] = React.useState(
    packageData.isActive !== false ? "true" : "false"
  );
  const [isDefaultValue, setIsDefaultValue] = React.useState(
    packageData.isDefault === true
  );
  const [showDefaultConfirm, setShowDefaultConfirm] = React.useState(false);
  const [pendingDefault, setPendingDefault] = React.useState(false);

  useEffect(() => {
    setIsDefaultValue(packageData.isDefault === true);
  }, [packageData.isDefault]);

  useEffect(() => {
    const form = formRef.current?.closest("form");
    if (form) {
      form.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }, [isDefaultValue]);

  const handleDefaultChange = async (checked: boolean) => {
    if (checked && !packageData.isDefault) {
      const packages = await getPackages(1, 500);
      const otherDefault = packages.find(
        (p) => p.id !== packageData.id && p.isDefault
      );
      if (otherDefault) {
        setPendingDefault(true);
        setShowDefaultConfirm(true);
        return;
      }
    }
    setIsDefaultValue(checked);
  };

  const handleConfirmDefault = () => {
    setIsDefaultValue(true);
    setShowDefaultConfirm(false);
    setPendingDefault(false);
  };

  return (
    <section className="mb-8" ref={formRef}>
      <ConfirmDialog
        open={showDefaultConfirm}
        onOpenChange={(open) => {
          setShowDefaultConfirm(open);
          if (!open) setPendingDefault(false);
        }}
        title="Pacote padrão"
        description="Outro pacote já está definido como padrão. Deseja substituir?"
        confirmText="Substituir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDefault}
      />
      <AutoSaveForm<UpdatePackageInputs>
        onSave={async (data) => {
          try {
            const normalized: UpdatePackageInputs = { ...data };
            if (typeof (normalized as any).isActive === "string") {
              (normalized as any).isActive = (normalized as any).isActive === "true";
            }
            if (typeof (normalized as any).isDefault === "string") {
              (normalized as any).isDefault = (normalized as any).isDefault === "true";
            }
            await onSave(normalized);
          } catch (error) {
            console.error("Erro ao salvar pacote:", error);
            throw error;
          }
        }}
        className="space-y-4"
      >
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Nome do Pacote
              </label>
              <Input
                type="text"
                name="name"
                defaultValue={packageData.name}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Descrição
              </label>
              <Textarea
                name="description"
                rows={4}
                defaultValue={packageData.description || ""}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Imagem do Banner
              </label>
              <ImageSelector
                key={packageData.id}
                name="image"
                defaultValue={extractImageFilename(packageData.imageUrl)}
                placeholder="URL da imagem do banner do pacote"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Status
              </label>
              <input type="hidden" name="isActive" value={isActiveValue} />
              <Select
                defaultValue={isActiveValue}
                onValueChange={setIsActiveValue}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Ativado</SelectItem>
                  <SelectItem value="false">Desativado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <input
                type="hidden"
                name="isDefault"
                value={isDefaultValue ? "true" : "false"}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={isDefaultValue}
                  onCheckedChange={handleDefaultChange}
                />
                <label className="text-sm font-medium text-neutral-700">
                  Pacote padrão
                </label>
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Ao se cadastrar, o estudante receberá este pacote automaticamente.
              </p>
            </div>
          </div>
        </Card>
      </AutoSaveForm>
    </section>
  );
};

export default PackageInfoForm;